import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import axios from 'axios';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [carouselRef, carouselVisible] = useScrollAnimation({ threshold: 0.1 });

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error('Failed to fetch testimonials:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const changeTestimonial = (newIndex) => {
    if (isAnimating || testimonials.length === 0) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setIsAnimating(false);
    }, 300);
  };

  const nextTestimonial = () => {
    if (testimonials.length === 0) return;
    const newIndex = (currentIndex + 1) % testimonials.length;
    changeTestimonial(newIndex);
  };

  const prevTestimonial = () => {
    if (testimonials.length === 0) return;
    const newIndex = (currentIndex - 1 + testimonials.length) % testimonials.length;
    changeTestimonial(newIndex);
  };

  // Show loading skeleton or hide only after loading complete with no data
  if (!isLoading && testimonials.length === 0) return null;

  return (
    <section className="testimonials-section" data-testid="testimonials-section">
      <div className="container">
        <div 
          ref={headerRef}
          className={`section-header animate-slide-up ${headerVisible ? 'is-visible' : ''}`}
        >
          <span className="section-tag">Success Stories</span>
          <h2 className="section-title">What Our Students Say</h2>
          <p className="section-subtitle">
            Join thousands of successful graduates who transformed their careers
          </p>
        </div>

        <div 
          ref={carouselRef}
          className={`testimonials-carousel animate-scale ${carouselVisible ? 'is-visible' : ''}`}
        >
          <button 
            className="carousel-btn prev hover-scale" 
            onClick={prevTestimonial}
            aria-label="Previous testimonial"
            data-testid="testimonial-prev-btn"
            disabled={isLoading || testimonials.length === 0}
          >
            <ChevronLeft size={24} />
          </button>

          <div className={`testimonial-card featured hover-lift ${isAnimating ? 'card-animating' : ''} ${isLoading ? 'skeleton-pulse' : ''}`}>
            {isLoading ? (
              <div className="testimonial-loading">
                <div className="skeleton-text" style={{ width: '100%', height: '100px' }}></div>
              </div>
            ) : (
              <>
                <Quote className="quote-icon" />
                <p className="testimonial-content">
                  {testimonials[currentIndex]?.content}
                </p>
                <div className="testimonial-rating">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="star-icon filled" />
                  ))}
                </div>
                <div className="testimonial-author">
                  <img 
                    src={testimonials[currentIndex]?.image} 
                    alt={testimonials[currentIndex]?.name}
                    className="author-image"
                  />
                  <div className="author-info">
                    <h4 className="author-name">{testimonials[currentIndex]?.name}</h4>
                    <p className="author-role">{testimonials[currentIndex]?.role}</p>
                  </div>
                </div>
              </>
            )}
          </div>

          <button 
            className="carousel-btn next hover-scale" 
            onClick={nextTestimonial}
            aria-label="Next testimonial"
            data-testid="testimonial-next-btn"
            disabled={isLoading || testimonials.length === 0}
          >
            <ChevronRight size={24} />
          </button>
        </div>

        <div className="carousel-dots">
          {isLoading ? (
            <div className="dot active"></div>
          ) : (
            testimonials.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => changeTestimonial(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
